import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { calculatePdfPagination, convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';
import { useTranslation } from 'react-i18next';
import { FaEye, FaPrint, FaFileExcel, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { getGeneralLedger, clearGeneralLedger, clearError, clearSuccess } from '../../../../redux/Slices/AccountingReportsSlice';
import { getPostingAccounts } from '../../../../redux/Slices/AccountTreeSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import {
  generatePdfFromElementIds,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from './components/PdfPreviewModal';

const GeneralLedger = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const { generalLedger, isLoading, error, success } = useSelector((state) => state.accountingReports);
  const { postingAccounts } = useSelector((state) => state.auth);

  const [accountId, setAccountId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const accountSearchInputRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [allEntries, setAllEntries] = useState([]);
  const [localPage, setLocalPage] = useState(1);
  const [pdfPages, setPdfPages] = useState([]);
  const itemsPerPage = 10;
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const SORT_FIELDS = { DATE: 'date', DESCRIPTION: 'description', DEBIT: 'debit', CREDIT: 'credit', BALANCE: 'balance', ACCOUNT: 'account_name', COST_CENTER: 'cost_center_name' };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort style={{ marginLeft: '4px', color: '#ccc', verticalAlign: 'middle' }} />;
    return sortOrder === 'asc'
      ? <FaSortUp style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />
      : <FaSortDown style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />;
  };

  useEffect(() => {
    setTitle('accountingReports.generalLedger', { defaultValue: 'دفتر الأستاذ العام' });
    dispatch(getPostingAccounts());
    return () => {
      dispatch(clearGeneralLedger());
    };
  }, [dispatch, setTitle]);

  const shouldShowToastRef = useRef(false);

  useEffect(() => {
    if (success && shouldShowToastRef.current) {
      toast.success(success, { rtl: isRTL });
      shouldShowToastRef.current = false;
      dispatch(clearSuccess());
    } else if (success) {
      // Clear success without showing toast for intermediate calls
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL]);

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);


  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (accountSearchTerm) {
        dispatch(getPostingAccounts(accountSearchTerm));
      } else {
        dispatch(getPostingAccounts());
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [accountSearchTerm, dispatch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountDropdownOpen && !e.target.closest('.account-dropdown-container')) {
        setAccountDropdownOpen(false);
      }
    };                                                                                                                                      
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [accountDropdownOpen]);

  const filteredAccounts = useMemo(() => {
    if (!Array.isArray(postingAccounts)) return [];
    if (!accountSearchTerm) return postingAccounts;
    const term = accountSearchTerm.toLowerCase();
    return postingAccounts.filter((account) => {
      const code = account.code?.toLowerCase() || '';
      const nameAr = account.name_ar?.toLowerCase() || '';
      const nameEn = account.name?.toLowerCase() || '';
      return code.includes(term) || nameAr.includes(term) || nameEn.includes(term);
    });
  }, [postingAccounts, accountSearchTerm]);

  const selectedAccount = postingAccounts?.find((acc) => acc.id === parseInt(accountId));

  // Update allEntries when generalLedger changes
  useEffect(() => {
    const reportData = generalLedger?.data || generalLedger || {};
    const movements = reportData?.movements || {};
    const currentEntries = Array.isArray(movements?.data)
      ? movements.data
      : Array.isArray(reportData?.entries)
        ? reportData.entries
        : Array.isArray(reportData?.data)
          ? reportData.data
          : Array.isArray(reportData)
            ? reportData
            : [];
    if (currentEntries.length > 0) {
      setAllEntries(currentEntries);
      setLocalPage(1);
    } else {
      setAllEntries([]);
    }
  }, [generalLedger]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accountId) {
      toast.error(t('accountingReports.selectAccountFirst', { defaultValue: 'برجاء اختيار الحساب أولاً' }), { rtl: isRTL });
      return;
    }
    // Reset flag - we'll set it to true only after the last call
    shouldShowToastRef.current = false;
    // Clear any previous success messages
    dispatch(clearSuccess());
    // First fetch to get total
    const result = await dispatch(
      getGeneralLedger({
        account_id: accountId,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page: 1,
      })
    );
    // Then fetch all with per_page = total
    if (result.payload?.data?.movements?.total) {
      const total = result.payload.data.movements.total;
      if (total > 0) {
        // Enable toast before the second call so it shows after it completes
        shouldShowToastRef.current = true;
        await dispatch(
          getGeneralLedger({
            account_id: accountId,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            per_page: total,
          })
        );
      } else {
        // If no data, show toast after first call
        shouldShowToastRef.current = true;
      }
    } else {
      // If no total, show toast after first call
      shouldShowToastRef.current = true;
    }
    setLocalPage(1);
  };

  const preparePdfPages = () => {
    const topHeader = document.getElementById('general-ledger-pdf-measure-top-header');
    const thead = document.getElementById('general-ledger-pdf-measure-thead');
    const footer = document.getElementById('general-ledger-pdf-measure-footer');
    const tbody = document.getElementById('general-ledger-pdf-measure-tbody');
    const chunks = calculatePdfPagination(allEntries, { topHeader, thead, footer, tbody }, { safeMargin: 25 });
    if (chunks.length === 0) {
      return [{ pageNumber: 1, entries: [], startIndex: 0, isFirstPage: true, isLastPage: true }];
    }
    return chunks.map((chunk, idx) => ({
      pageNumber: idx + 1,
      entries: chunk.items || chunk,
      startIndex: chunk.startIndex ?? idx * 10,
      isFirstPage: idx === 0,
      isLastPage: idx === chunks.length - 1,
    }));
  };

  const handlePreviewAll = () => {
    if (!generalLedger) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    const pages = preparePdfPages();
    setPdfPages(pages);
    setIsPreviewOpen(true);
  };

  const handlePrintAll = async () => {
    if (!generalLedger) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      setPdfType('all');
      const pages = preparePdfPages();
      setPdfPages(pages);

      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsGeneratingPdf(true);

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const pageElementIds = pages.map((_, i) =>
        document.getElementById(`general-ledger-pdf-all-${i}`)
          ? `general-ledger-pdf-all-${i}`
          : `preview-general-ledger-pdf-all-${i}`,
      );
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_GeneralLedger_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
      toast.success(t('accountingReports.printed', { defaultValue: 'تم طباعة التقرير بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(t('accountingReports.printError', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfPages([]);
    }
  };

  const handleExportAllToExcel = () => {
    if (!generalLedger) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();

      const detailRows = sortedEntries.map((row) => ({
        [t('accountingReports.date', { defaultValue: 'التاريخ' })]: formatDate(row.journal_entry?.entry_date || row.date || row.transaction_date),
        [t('accountingReports.description', { defaultValue: 'الوصف' })]: row.line_description || row.journal_entry?.description || row.description || row.details || '',
        [t('accountingReports.debit', { defaultValue: 'مدين' })]: parseFloat(row.debit) || 0,
        [t('accountingReports.credit', { defaultValue: 'دائن' })]: parseFloat(row.credit) || 0,
        [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: parseFloat(row.balance) || 0,
        [t('accountingReports.account', { defaultValue: 'الحساب' })]: row.account_name || row.account || '',
        [t('accountingReports.costCenter', { defaultValue: 'مركز التكلفة' })]: row.cost_center_name || row.cost_center || ''
      }));

      if (detailRows.length === 0) {
        detailRows.push({
          [t('accountingReports.date', { defaultValue: 'التاريخ' })]: '',
          [t('accountingReports.description', { defaultValue: 'الوصف' })]: t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' }),
          [t('accountingReports.debit', { defaultValue: 'مدين' })]: 0,
          [t('accountingReports.credit', { defaultValue: 'دائن' })]: 0,
          [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: 0,
          [t('accountingReports.account', { defaultValue: 'الحساب' })]: '',
          [t('accountingReports.costCenter', { defaultValue: 'مركز التكلفة' })]: ''
        });
      }

      const totalsRow = {
        [t('accountingReports.date', { defaultValue: 'التاريخ' })]: '',
        [t('accountingReports.description', { defaultValue: 'الوصف' })]: t('accountingReports.total', { defaultValue: 'الإجمالي' }),
        [t('accountingReports.debit', { defaultValue: 'مدين' })]: totals.total_debit ?? totals.debit ?? 0,
        [t('accountingReports.credit', { defaultValue: 'دائن' })]: totals.total_credit ?? totals.credit ?? 0,
        [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: totals.closing_balance ?? totals.total_balance ?? totals.balance ?? 0,
        [t('accountingReports.account', { defaultValue: 'الحساب' })]: '',
        [t('accountingReports.costCenter', { defaultValue: 'مركز التكلفة' })]: ''
      };
      detailRows.push(totalsRow);

      const detailsSheet = XLSX.utils.json_to_sheet(detailRows);
      XLSX.utils.book_append_sheet(workbook, detailsSheet, t('accountingReports.generalLedger', { defaultValue: 'دفتر الأستاذ' }));

      const summaryRows = [];
      if (totals?.opening_balance !== undefined) {
        summaryRows.push({
          [t('accountingReports.item', { defaultValue: 'البند' })]: t('accountingReports.openingBalance', { defaultValue: 'الرصيد الافتتاحي' }),
          [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: totals.opening_balance
        });
      }
      summaryRows.push(
        {
          [t('accountingReports.item', { defaultValue: 'البند' })]: t('accountingReports.totalDebit', { defaultValue: 'إجمالي المدين' }),
          [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: totals.total_debit ?? totals.debit ?? 0
        },
        {
          [t('accountingReports.item', { defaultValue: 'البند' })]: t('accountingReports.totalCredit', { defaultValue: 'إجمالي الدائن' }),
          [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: totals.total_credit ?? totals.credit ?? 0
        }
      );
      if (totals?.closing_balance !== undefined) {
        summaryRows.push({
          [t('accountingReports.item', { defaultValue: 'البند' })]: t('accountingReports.closingBalance', { defaultValue: 'الرصيد الختامي' }),
          [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: totals.closing_balance
        });
      }
      summaryRows.push(
        {
          [t('accountingReports.item', { defaultValue: 'البند' })]: t('accountingReports.period', { defaultValue: 'الفترة' }),
          [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: meta?.start_date && meta?.end_date ? `${formatDate(meta.start_date)} - ${formatDate(meta.end_date)}` : `${formatDate(startDate)} - ${formatDate(endDate)}`
        },
        {
          [t('accountingReports.item', { defaultValue: 'البند' })]: t('accountingReports.account', { defaultValue: 'الحساب' }),
          [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: selectedAccount ? (isRTL ? selectedAccount.name_ar || selectedAccount.name : selectedAccount.name || selectedAccount.name_ar) : '—'
        }
      );
      const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
      XLSX.utils.book_append_sheet(workbook, summarySheet, t('accountingReports.summary', { defaultValue: 'ملخص' }));

      XLSX.writeFile(workbook, `GeneralLedger_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(t('accountingReports.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('accountingReports.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const formatNumber = (value) =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return convertArabicNumbersToEnglish(
      new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    );
  };

  const reportData = generalLedger?.data || generalLedger || {};
  const totals = reportData?.totals || {};
  const meta = reportData?.meta || {};

  const sortedEntries = useMemo(() => {
    if (!sortField) return allEntries;
    return [...allEntries].sort((a, b) => {
      let aVal = a[sortField] ?? '';
      let bVal = b[sortField] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [allEntries, sortField, sortOrder]);

  // Local pagination
  const totalPages = Math.ceil(sortedEntries.length / itemsPerPage);
  const paginatedEntries = useMemo(() => {
    const startIndex = (localPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedEntries.slice(startIndex, endIndex);
  }, [sortedEntries, localPage]);

  const handleLocalPageChange = (page) => {
    setLocalPage(page);
  };

  const entries = paginatedEntries.length > 0 ? paginatedEntries : [];

  return (
    <div style={{ padding: '20px', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ 
        backgroundColor: '#1b3350', 
        borderRadius: '12px', 
        padding: '24px',
        border: '1px solid #333'
      }}>
        <h2 style={{ color: 'white', marginBottom: '20px', direction: dir, textAlign }}>
          {t('accountingReports.generalLedger', { defaultValue: 'دفتر الأستاذ العام' })}
        </h2>

        {/* Filters */}
        <form onSubmit={handleSubmit} style={{ 
          backgroundColor: '#1a1f2e', 
          padding: '20px', 
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
            <div className="account-dropdown-container" style={{ position: 'relative' }}>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.account', { defaultValue: 'الحساب' })}
              </label>
              <div
                onClick={() => {
                  setAccountDropdownOpen(!accountDropdownOpen);
                  if (!accountDropdownOpen && accountSearchInputRef.current) {
                    setTimeout(() => accountSearchInputRef.current?.focus(), 100);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{selectedAccount ? (isRTL ? selectedAccount.name_ar || selectedAccount.name : selectedAccount.name || selectedAccount.name_ar) : t('accountingReports.selectAccount', { defaultValue: 'اختر الحساب' })}</span>
                <span style={{ fontSize: '12px' }}>▼</span>
              </div>
              {accountDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '300px',
                  overflow: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  <input
                    ref={accountSearchInputRef}
                    type="text"
                    placeholder={t('accountingReports.search', { defaultValue: 'بحث...' })}
                    value={accountSearchTerm}
                    onChange={(e) => {
                      e.stopPropagation();
                      setAccountSearchTerm(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#14263b',
                      border: 'none',
                      borderBottom: '1px solid #333',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                  <div style={{ maxHeight: '250px', overflow: 'auto' }}>
                    {filteredAccounts.length > 0 ? (
                      filteredAccounts.map((account) => (
                        <div
                          key={account.id}
                          onClick={() => {
                            setAccountId(account.id.toString());
                            setAccountDropdownOpen(false);
                            setAccountSearchTerm('');
                          }}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            color: 'white',
                            borderBottom: '1px solid #333',
                            backgroundColor: accountId === account.id.toString() ? '#AC2000' : 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            if (accountId !== account.id.toString()) {
                              e.currentTarget.style.backgroundColor = '#2a3441';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (accountId !== account.id.toString()) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          {isRTL ? (account.name_ar || account.name || '') : (account.name || account.name_ar || '')} {account.code ? `(${account.code})` : ''}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '10px', color: '#999', textAlign: 'center' }}>
                        {t('accountingReports.noAccounts', { defaultValue: 'لا توجد حسابات' })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.startDate', { defaultValue: 'تاريخ البداية' })}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.endDate', { defaultValue: 'تاريخ النهاية' })}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? '#666' : '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
                opacity: isLoading ? 0.6 : 1,
                minWidth: '160px'
            }}
          >
            {isLoading ? t('accountingReports.loading', { defaultValue: 'جاري التحميل...' }) : t('accountingReports.view', { defaultValue: 'عرض' })}
          </button>
          </div>
        </form>

        {/* Action Buttons */}
        {generalLedger && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: '#444',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaEye /> {t('accountingReports.previewAllPDF', { defaultValue: 'عرض PDF للكل' })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaPrint /> {isGeneratingPdf ? t('accountingReports.generating', { defaultValue: 'جاري التوليد...' }) : t('accountingReports.printAllPDF', { defaultValue: 'طباعة PDF للكل' })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaFileExcel /> {t('accountingReports.exportExcel', { defaultValue: 'تصدير Excel للكل' })}
            </button>
          </div>
        )}

        {/* Report Data Display */}
        {generalLedger && (
          <div style={{ 
            backgroundColor: '#1a1f2e', 
            padding: '20px', 
            borderRadius: '8px',
            marginTop: '20px'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
                <div style={{ 
                  backgroundColor: '#252f3f', 
                  padding: '15px', 
                  borderRadius: '8px',
                  border: '1px solid #333',
                  direction: dir
                }}>
                  <div style={{ color: '#999', fontSize: '12px', marginBottom: '5px', textAlign }}>
                    {t('accountingReports.account', { defaultValue: 'الحساب' })}
                  </div>
                  <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold', textAlign }}>
                    {selectedAccount ? (isRTL ? selectedAccount.name_ar || selectedAccount.name : selectedAccount.name || selectedAccount.name_ar) : '—'}
                  </div>
                  {selectedAccount?.code && (
                    <div style={{ color: '#999', fontSize: '12px', marginTop: '5px', textAlign }}>
                      {selectedAccount.code}
                    </div>
                  )}
                </div>
                <div style={{ 
                  backgroundColor: '#252f3f', 
                  padding: '15px', 
                  borderRadius: '8px',
                  border: '1px solid #333',
                  direction: dir
                }}>
                  <div style={{ color: '#999', fontSize: '12px', marginBottom: '5px', textAlign }}>
                    {t('accountingReports.period', { defaultValue: 'الفترة' })}
                  </div>
                  <div style={{ color: '#fff', fontSize: '16px', fontWeight: 'bold', textAlign }}>
                    {meta?.start_date && meta?.end_date ? `${formatDate(meta.start_date)} - ${formatDate(meta.end_date)}` : `${formatDate(startDate)} - ${formatDate(endDate)}`}
                  </div>
                </div>
                {totals?.opening_balance !== undefined && (
                  <div style={{ 
                    backgroundColor: '#1e3a5f', 
                    padding: '15px', 
                    borderRadius: '8px',
                    border: '1px solid #2a4a6f',
                    direction: dir
                  }}>
                    <div style={{ color: '#a0c4ff', fontSize: '12px', marginBottom: '5px', textAlign }}>
                      {t('accountingReports.openingBalance', { defaultValue: 'الرصيد الافتتاحي' })}
                    </div>
                    <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold', textAlign }}>
                      {formatNumber(totals.opening_balance)}
                    </div>
                  </div>
                )}
                {totals?.closing_balance !== undefined && (
                  <div style={{ 
                    backgroundColor: '#1e5f3a', 
                    padding: '15px', 
                    borderRadius: '8px',
                    border: '1px solid #2a6f4a',
                    direction: dir
                  }}>
                    <div style={{ color: '#a0ffc4', fontSize: '12px', marginBottom: '5px', textAlign }}>
                      {t('accountingReports.closingBalance', { defaultValue: 'الرصيد الختامي' })}
                    </div>
                    <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold', textAlign }}>
                      {formatNumber(totals.closing_balance)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ overflowX: 'auto', backgroundColor: '#111826', borderRadius: '8px', padding: '12px', direction: dir }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', color: '#eee', fontSize: '13px', direction: dir }}>
                <thead>
                  <tr style={{ backgroundColor: '#252f3f' }}>
                    <th style={{ borderBottom: '1px solid #333', padding: '10px', textAlign: 'center', width: '36px' }}>#</th>
                    <th style={{ borderBottom: '1px solid #333', padding: '10px', textAlign, cursor: 'pointer' }} onClick={() => handleSort('date')}>{t('accountingReports.date', { defaultValue: 'التاريخ' })}{getSortIcon('date')}</th>
                    <th style={{ borderBottom: '1px solid #333', padding: '10px', textAlign }}>{t('accountingReports.description', { defaultValue: 'الوصف' })}</th>
                    <th style={{ borderBottom: '1px solid #333', padding: '10px', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('debit')}>{t('accountingReports.debit', { defaultValue: 'مدين' })}{getSortIcon('debit')}</th>
                    <th style={{ borderBottom: '1px solid #333', padding: '10px', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('credit')}>{t('accountingReports.credit', { defaultValue: 'دائن' })}{getSortIcon('credit')}</th>
                    <th style={{ borderBottom: '1px solid #333', padding: '10px', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('balance')}>{t('accountingReports.balance', { defaultValue: 'الرصيد' })}{getSortIcon('balance')}</th>
                    <th style={{ borderBottom: '1px solid #333', padding: '10px', textAlign }}>{t('accountingReports.account', { defaultValue: 'الحساب' })}</th>
                    <th style={{ borderBottom: '1px solid #333', padding: '10px', textAlign }}>{t('accountingReports.costCenter', { defaultValue: 'مركز التكلفة' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {allEntries.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ padding: '12px', textAlign: 'center', color: '#bbb' }}>
                        {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                      </td>
                    </tr>
                  )}
                  {entries.map((row, idx) => {
                    const debit = parseFloat(row.debit) || 0;
                    const credit = parseFloat(row.credit) || 0;
                    const balance = parseFloat(row.balance) || 0;
                    return (
                      <tr key={row.id || row.entry_id || idx} style={{ borderBottom: '1px solid #222' }}>
                        <td style={{ padding: '10px', textAlign: 'center' }}>{(localPage - 1) * itemsPerPage + idx + 1}</td>
                        <td style={{ padding: '10px', textAlign }}>{formatDate(row.journal_entry?.entry_date || row.date || row.transaction_date)}</td>
                        <td style={{ padding: '10px', textAlign }}>{row.line_description || row.journal_entry?.description || row.description || row.details || '—'}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>{formatNumber(debit)}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>{formatNumber(credit)}</td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 'bold' }}>{formatNumber(balance)}</td>
                        <td style={{ padding: '10px', textAlign }}>{row.account_name || row.account || '—'}</td>
                        <td style={{ padding: '10px', textAlign }}>{row.cost_center_name || row.cost_center || '—'}</td>
                      </tr>
                    );
                  })}
                  <tr style={{ backgroundColor: '#1f2937', fontWeight: 'bold' }}>
                    <td style={{ padding: '10px', textAlign }} colSpan={3}>
                      {t('accountingReports.total', { defaultValue: 'الإجمالي' })}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{formatNumber(totals.total_debit ?? totals.debit ?? 0)}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{formatNumber(totals.total_credit ?? totals.credit ?? 0)}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{formatNumber(totals.closing_balance ?? totals.total_balance ?? totals.balance ?? 0)}</td>
                    <td style={{ padding: '10px', textAlign }} colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            </div>
            {accountId && allEntries.length > 0 && totalPages > 1 && (
              <div
                style={{
                  padding: '18px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  borderTop: '1px solid #333',
                }}
              >
                <button
                  onClick={() => handleLocalPageChange(Math.max(1, localPage - 1))}
                  disabled={localPage === 1}
                  style={{
                    backgroundColor: '#1b3350',
                    color: 'white',
                    border: '1px solid #333',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    cursor: localPage > 1 ? 'pointer' : 'not-allowed',
                    opacity: localPage > 1 ? 1 : 0.5,
                    fontSize: '14px',
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={`page-${page}`}
                    onClick={() => handleLocalPageChange(page)}
                    style={{
                      backgroundColor:
                        page === localPage ? '#AC2000' : '#1b3350',
                      color: 'white',
                      border:
                        page === localPage ? 'none' : '1px solid #333',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: page === localPage ? 'bold' : 'normal',
                    }}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handleLocalPageChange(Math.min(totalPages, localPage + 1))}
                  disabled={localPage === totalPages}
                  style={{
                    backgroundColor: '#1b3350',
                    color: 'white',
                    border: '1px solid #333',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    cursor: localPage < totalPages ? 'pointer' : 'not-allowed',
                    opacity: localPage < totalPages ? 1 : 0.5,
                    fontSize: '14px',
                  }}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PDF Print Templates (one full page per chunk) */}
      {pdfType === 'all' && generalLedger && pdfPages && pdfPages.length > 0 && (
        pdfPages.map((page, pageIndex) => (
          <GeneralLedgerPdfPage
            key={pageIndex}
            idPrefix="general-ledger-pdf-all"
            page={page}
            pageIndex={pageIndex}
            isRTL={isRTL}
            dir={dir}
            t={t}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            selectedAccount={selectedAccount}
            startDate={startDate}
            endDate={endDate}
            totals={totals}
            formatNumber={formatNumber}
            formatDate={formatDate}
            hidden
          />
        ))
      )}

      <PdfPreviewModal
        isOpen={isPreviewOpen && !!generalLedger}
        title={t('accountingReports.generalLedgerTitle', { defaultValue: 'دفتر الأستاذ العام' })}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
        contentWidth="794px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          {pdfPages && pdfPages.length > 0 && pdfPages.map((page, pageIndex) => (
            <GeneralLedgerPdfPage
              key={pageIndex}
              idPrefix="preview-general-ledger-pdf-all"
              page={page}
              pageIndex={pageIndex}
              isRTL={isRTL}
              dir={dir}
              t={t}
              publicUrl={publicUrl}
              headerLogo={headerLogo}
              selectedAccount={selectedAccount}
              startDate={startDate}
              endDate={endDate}
              totals={totals}
              formatNumber={formatNumber}
              formatDate={formatDate}
            />
          ))}
        </div>
      </PdfPreviewModal>

      {/* Hidden measurement template used to calculate dynamic PDF pagination */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', pointerEvents: 'none', visibility: 'hidden' }}>
        <div id="general-ledger-pdf-measure-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
          <div style={{ marginTop: '8px', textAlign: 'center', marginBottom: '12px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 4px 0' }}>{t('accountingReports.generalLedger', { defaultValue: 'دفتر الأستاذ العام' })}</h2>
            {selectedAccount && (
              <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '2px' }}>{isRTL ? selectedAccount.name_ar || selectedAccount.name : selectedAccount.name || selectedAccount.name_ar}</div>
            )}
            {(startDate || endDate) && (
              <div style={{ fontSize: '11px' }}>{formatDate(startDate)} — {formatDate(endDate)}</div>
            )}
          </div>
          {/* Reserve space for the first-page opening/closing balance summary cards so pagination never overflows into the footer on page 1 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '14px' }}>
            <div style={{ padding: '8px', border: '1px solid #000', borderRadius: '4px' }}>
              <div style={{ fontSize: '11px', marginBottom: '4px' }}>x</div>
              <div style={{ fontSize: '13px', fontWeight: '700' }}>0.00</div>
            </div>
            <div style={{ padding: '8px', border: '1px solid #000', borderRadius: '4px' }}>
              <div style={{ fontSize: '11px', marginBottom: '4px' }}>x</div>
              <div style={{ fontSize: '13px', fontWeight: '700' }}>0.00</div>
            </div>
            <div style={{ padding: '8px', border: '1px solid #000', borderRadius: '4px' }}>
              <div style={{ fontSize: '11px', marginBottom: '4px' }}>x</div>
              <div style={{ fontSize: '13px', fontWeight: '700' }}>0.00</div>
            </div>
            <div style={{ padding: '8px', border: '1px solid #000', borderRadius: '4px' }}>
              <div style={{ fontSize: '11px', marginBottom: '4px' }}>x</div>
              <div style={{ fontSize: '13px', fontWeight: '700' }}>0.00</div>
            </div>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', border: '1px solid #000' }}>
          <thead id="general-ledger-pdf-measure-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '5px 4px' }}>#</th>
              <th style={{ border: '1px solid #000', padding: '5px 4px' }}>{t('accountingReports.date', { defaultValue: 'التاريخ' })}</th>
              <th style={{ border: '1px solid #000', padding: '5px 4px' }}>{t('accountingReports.description', { defaultValue: 'الوصف' })}</th>
              <th style={{ border: '1px solid #000', padding: '5px 4px' }}>{t('accountingReports.debit', { defaultValue: 'مدين' })}</th>
              <th style={{ border: '1px solid #000', padding: '5px 4px' }}>{t('accountingReports.credit', { defaultValue: 'دائن' })}</th>
              <th style={{ border: '1px solid #000', padding: '5px 4px' }}>{t('accountingReports.balance', { defaultValue: 'الرصيد' })}</th>
              <th style={{ border: '1px solid #000', padding: '5px 4px' }}>{t('accountingReports.account', { defaultValue: 'الحساب' })}</th>
              <th style={{ border: '1px solid #000', padding: '5px 4px' }}>{t('accountingReports.costCenter', { defaultValue: 'مركز التكلفة' })}</th>
            </tr>
          </thead>
          <tbody id="general-ledger-pdf-measure-tbody">
            {allEntries.slice(0, 5).map((row, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{idx + 1}</td>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{row.date || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{row.description || row.line_description || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{row.debit || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{row.credit || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{row.balance || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{row.account_name || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '4px' }}>{row.cost_center_name || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div id="general-ledger-pdf-measure-footer" style={{ height: '120px' }} />
      </div>
    </div>
  );
};

const GeneralLedgerPdfPage = ({
  idPrefix,
  page,
  pageIndex,
  isRTL,
  dir,
  t,
  publicUrl,
  headerLogo,
  selectedAccount,
  startDate,
  endDate,
  totals,
  formatNumber,
  formatDate,
  hidden = false,
}) => {
  const entries = page.entries || [];
  return (
    <div
      id={`${idPrefix}-${pageIndex}`}
      aria-hidden={hidden || undefined}
      style={{
        position: hidden ? 'absolute' : 'static',
        left: hidden ? '-9999px' : undefined,
        top: hidden ? 0 : undefined,
        width: '794px',
        minHeight: '1123px',
        height: 'auto',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        overflow: 'visible',
        boxShadow: hidden ? undefined : '0 4px 20px rgba(0, 0, 0, 0.5)',
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '1043px',
        height: 'auto',
        overflow: 'visible'
      }}>
        <div style={{
          border: '2px solid #000',
          minHeight: '1033px',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'visible'
        }}>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '600px',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.1,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            />

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '8px', textAlign: 'center', marginBottom: '12px', direction: dir }}>
                <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#000080', margin: '0 0 4px 0' }}>
                  {t('accountingReports.generalLedger', { defaultValue: 'دفتر الأستاذ العام' })}
                </h2>
                {selectedAccount && (
                  <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '2px' }}>
                    {isRTL ? selectedAccount.name_ar || selectedAccount.name : selectedAccount.name || selectedAccount.name_ar}
                  </div>
                )}
                {(startDate || endDate) && (
                  <div style={{ fontSize: '11px', color: '#444' }}>
                    {formatDate(startDate)} — {formatDate(endDate)}
                  </div>
                )}
              </div>

              {page.isFirstPage && (totals.opening_balance !== undefined || totals.closing_balance !== undefined) && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ padding: '8px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5' }}>
                    <div style={{ fontSize: '11px', marginBottom: '4px' }}>{t('accountingReports.openingBalance', { defaultValue: 'الرصيد الافتتاحي' })}</div>
                    <div style={{ fontSize: '13px', fontWeight: '700' }}>{formatNumber(totals.opening_balance || 0)}</div>
                  </div>
                  <div style={{ padding: '8px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5' }}>
                    <div style={{ fontSize: '11px', marginBottom: '4px' }}>{t('accountingReports.totalDebit', { defaultValue: 'إجمالي مدين' })}</div>
                    <div style={{ fontSize: '13px', fontWeight: '700' }}>{formatNumber(totals.total_debit ?? totals.debit ?? 0)}</div>
                  </div>
                  <div style={{ padding: '8px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5' }}>
                    <div style={{ fontSize: '11px', marginBottom: '4px' }}>{t('accountingReports.totalCredit', { defaultValue: 'إجمالي دائن' })}</div>
                    <div style={{ fontSize: '13px', fontWeight: '700' }}>{formatNumber(totals.total_credit ?? totals.credit ?? 0)}</div>
                  </div>
                  <div style={{ padding: '8px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5' }}>
                    <div style={{ fontSize: '11px', marginBottom: '4px' }}>{t('accountingReports.closingBalance', { defaultValue: 'الرصيد الختامي' })}</div>
                    <div style={{ fontSize: '13px', fontWeight: '700' }}>{formatNumber(totals.closing_balance ?? totals.total_balance ?? totals.balance ?? 0)}</div>
                  </div>
                </div>
              )}

              <div style={{ color: '#000', fontSize: '11px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', direction: isRTL ? 'rtl' : 'ltr', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '30px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '160px' }} />
                    <col style={{ width: '70px' }} />
                    <col style={{ width: '70px' }} />
                    <col style={{ width: '70px' }} />
                    <col style={{ width: '100px' }} />
                    <col style={{ width: '90px' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: '#d1d5db' }}>
                      <th style={{ border: '1px solid #000', padding: '5px 4px', textAlign: 'center', fontSize: '10px', fontWeight: '700' }}>#</th>
                      <th style={{ border: '1px solid #000', padding: '5px 4px', textAlign: isRTL ? 'right' : 'left', fontSize: '10px', fontWeight: '700' }}>
                        {t('accountingReports.date', { defaultValue: 'التاريخ' })}
                      </th>
                      <th style={{ border: '1px solid #000', padding: '5px 4px', textAlign: isRTL ? 'right' : 'left', fontSize: '10px', fontWeight: '700' }}>
                        {t('accountingReports.description', { defaultValue: 'الوصف' })}
                      </th>
                      <th style={{ border: '1px solid #000', padding: '5px 4px', textAlign: 'center', fontSize: '10px', fontWeight: '700' }}>
                        {t('accountingReports.debit', { defaultValue: 'مدين' })}
                      </th>
                      <th style={{ border: '1px solid #000', padding: '5px 4px', textAlign: 'center', fontSize: '10px', fontWeight: '700' }}>
                        {t('accountingReports.credit', { defaultValue: 'دائن' })}
                      </th>
                      <th style={{ border: '1px solid #000', padding: '5px 4px', textAlign: 'center', fontSize: '10px', fontWeight: '700' }}>
                        {t('accountingReports.balance', { defaultValue: 'الرصيد' })}
                      </th>
                      <th style={{ border: '1px solid #000', padding: '5px 4px', textAlign: isRTL ? 'right' : 'left', fontSize: '10px', fontWeight: '700' }}>
                        {t('accountingReports.account', { defaultValue: 'الحساب' })}
                      </th>
                      <th style={{ border: '1px solid #000', padding: '5px 4px', textAlign: isRTL ? 'right' : 'left', fontSize: '10px', fontWeight: '700' }}>
                        {t('accountingReports.costCenter', { defaultValue: 'مركز التكلفة' })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 && (
                      <tr>
                        <td colSpan={8} style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                          {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                        </td>
                      </tr>
                    )}
                    {entries.map((row, idx) => {
                      const debit = parseFloat(row.debit) || 0;
                      const credit = parseFloat(row.credit) || 0;
                      const balance = parseFloat(row.balance) || 0;
                      return (
                        <tr key={row.id || row.entry_id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center', fontSize: '9px' }}>{page.startIndex + idx + 1}</td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: isRTL ? 'right' : 'left', fontSize: '9px', whiteSpace: 'nowrap' }}>{formatDate(row.journal_entry?.entry_date || row.date || row.transaction_date)}</td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: isRTL ? 'right' : 'left', fontSize: '9px', wordBreak: 'break-word' }}>{row.line_description || row.journal_entry?.description || row.description || row.details || '—'}</td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center', fontSize: '9px', whiteSpace: 'nowrap' }}>{formatNumber(debit)}</td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center', fontSize: '9px', whiteSpace: 'nowrap' }}>{formatNumber(credit)}</td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center', fontSize: '9px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{formatNumber(balance)}</td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: isRTL ? 'right' : 'left', fontSize: '9px', wordBreak: 'break-word' }}>{row.account_name || row.account || '—'}</td>
                          <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: isRTL ? 'right' : 'left', fontSize: '9px', wordBreak: 'break-word' }}>{row.cost_center_name || row.cost_center || '—'}</td>
                        </tr>
                      );
                    })}
                    {page.isLastPage && (
                      <tr style={{ backgroundColor: '#e5e7eb', fontWeight: 'bold' }}>
                        <td style={{ border: '1px solid #000', padding: '5px 4px', textAlign: isRTL ? 'right' : 'left', fontSize: '10px', fontWeight: '700' }} colSpan={3}>
                          {t('accountingReports.total', { defaultValue: 'الإجمالي' })}
                        </td>
                        <td style={{ border: '1px solid #000', padding: '5px 4px', textAlign: 'center', fontSize: '10px', fontWeight: '700' }}>{formatNumber(totals.total_debit ?? totals.debit ?? 0)}</td>
                        <td style={{ border: '1px solid #000', padding: '5px 4px', textAlign: 'center', fontSize: '10px', fontWeight: '700' }}>{formatNumber(totals.total_credit ?? totals.credit ?? 0)}</td>
                        <td style={{ border: '1px solid #000', padding: '5px 4px', textAlign: 'center', fontSize: '10px', fontWeight: '700' }}>{formatNumber(totals.closing_balance ?? totals.total_balance ?? totals.balance ?? 0)}</td>
                        <td style={{ border: '1px solid #000', padding: '5px 4px' }} colSpan={2}></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{
            position: 'absolute',
            bottom: '10px',
            left: '18px',
            right: '18px',
            flexShrink: 0
          }}>
            <img
              src={`${publicUrl}/FooterAce.png`}
              alt="Footer"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                borderTop: '4px double #000',
                paddingTop: '8px',
              }}
              crossOrigin="anonymous"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralLedger;
